
import { Component, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { reportError, getCurrentUserId } from "./utils/helpers";
import { COMPONENT_VISIBILITY } from "./types";
import LoadingStateController from "./utils/LoadingStateController";
import TopBar from "./components/TopBar.vue";
import EventBus from "./utils/EventBus";
import { ToastPayload } from "./utils/toast";
import ProfilesRepository from "./repositories/implementations/ProfilesRepository";
import DatabaseProvider from "./providers/implementations/DatabaseProvider";
import User from "./entities/User";

@Component({
  components: {
    TopBar,
  },
})
export default class App extends Vue {
  @Getter("app/fullscreenLoaderText") fullscreenLoaderText: string;

  @Getter("auth/user") user: null | User;

  @Getter("app/topBarVisibility") topBarVisibility: string;

  fatalErrorMessage = "";

  databaseProvider = new DatabaseProvider();

  toast = {
    visible: false,
    message: "",
    color: "success",
    timeout: 4000,
  };

  get isTopBarVisible() {
    return this.topBarVisibility === COMPONENT_VISIBILITY.visible;
  }

  get showFullscreenLoader() {
    return LoadingStateController.state.isLoading && this.$route.name !== "home";
  }

  created() {
    const userId = getCurrentUserId();
    if (userId) {
      this.loadServerData(userId);
    }

    EventBus.$on("local-user-changed", async ({ newUser }: Record<string, User>) => {
      const userIsSignedIn = newUser && newUser?.id;
      if (userIsSignedIn) {
        this.loadServerData(newUser.id as string);
      }
    });

    EventBus.$on("toast", (payload: ToastPayload) => {
      this.toast.message = payload.message;
      this.toast.color = payload.color;
      this.toast.timeout = payload.timeout ?? (payload.color === "error" ? -1 : 4000);
      this.toast.visible = true;
    });
  }

  async loadServerData(userId: string) {
    LoadingStateController.start("loadServerData");

    const profileLoaded = await this.loadProfile(userId);

    const routesToIgnoreProfile = ["sign-in", "sign-out"];
    if (!profileLoaded && !routesToIgnoreProfile.includes(this.$route.name || "")) {
      this.fatalErrorMessage = this.$t("error_loading_profile") as string;
      LoadingStateController.stop("loadServerData");
      return;
    }

    LoadingStateController.stop("loadServerData");
  }

  async loadProfile(userId: string): Promise<boolean> {
    try {
      const profilesRepository = new ProfilesRepository(this.databaseProvider);
      const profile = await profilesRepository.getByUserId(userId);

      if (!profile) {
        throw new Error(`Profile couldn't be loaded for userId '${userId}'`);
      }

      EventBus.$emit("profile-loaded", profile);
      return true;
    } catch (error: any) {
      reportError(`[loadProfile] Error loading profile for userId "${userId}"`, { userId, error });
      return false;
    }
  }
}
